import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Form as FormikForm, Formik } from 'formik';
import * as React from 'react';
import * as Yup from 'yup';
import { SwitchInput } from '../../../../../../components/CustomFields/Switch';
import { FormEditable } from '../../../../../../components/Form/components/FormEditable';
import { type OnSubmit, Section, useWizard } from '../../../../../../design-system';
import type { FormFields, FormValues } from '../../../../../../utils/formUtils';
import { combineSchemas, createSchema } from '../../../../../../utils/formUtils';
import type { Details } from '../types';

type DetailsStepProps<Fields extends FormValues, HelperText extends React.ReactNode> = {
  initialValues: Fields;
  fields: FormFields<Fields, HelperText>;
  validationSchema: Yup.ObjectSchema<Partial<Fields>, Fields>;
};

const detailsValidationSchema = createSchema<Details>({
  name: Yup.string()
    .required('Name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(80, 'Name must be at most 80 characters'),
  referenceId: Yup.string()
    .min(1, 'Reference ID must be at least 1 character')
    .max(80, 'Reference ID must be at most 80 characters')
    .matches(/^[a-zA-Z0-9-_. ]*$/, 'Reference ID may only include numbers, letters, "-", "_", "." and " " characters'),
  authorizationRequired: Yup.boolean().required('Please indicate whether authorisation is required to start charging'),
});

const detailsFields: FormFields<Omit<Details, 'authorizationRequired'>> = {
  name: {
    id: 'name',
    label: 'Name',
    inputProps: { helperText: 'A recognisable name for this charge point, e.g. "Garage charger" or "Parking spot #1"' },
  },
  referenceId: {
    id: 'referenceId',
    label: 'Reference ID',
    inputProps: { helperText: '(Optional) A custom identifier for this charge point' },
  },
};

const configurationFields: FormFields<Pick<Details, 'authorizationRequired'>> = {
  authorizationRequired: {
    id: 'authorizationRequired',
    label: 'Authorization required',
    inputComponent: SwitchInput,
    inputProps: { helperText: 'Whether a valid RFID card must be swiped before a charging session can begin' },
  },
};

export function DetailsStepForm<Fields extends FormValues, HelperText extends React.ReactNode>({
  children,
  fields,
  initialValues,
  validationSchema,
}: React.PropsWithChildren<DetailsStepProps<Fields, HelperText>>) {
  const { state, setState, prevStep, nextStep } = useWizard();

  const formFields: FormFields<Fields, HelperText> = {
    name: detailsFields.name,
    ...fields,
    referenceId: detailsFields.referenceId,
  };

  const onSubmit = React.useCallback<OnSubmit<Fields>>(
    (update) => {
      setState(update);
      nextStep();
    },
    [setState, nextStep],
  );

  return (
    <Formik<Fields>
      initialValues={{ name: '', referenceId: '', authorizationRequired: false, ...initialValues, ...state }}
      validationSchema={combineSchemas(detailsValidationSchema, validationSchema)}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting }) => (
        <FormikForm>
          <Section title="Charge point details">
            <FormEditable fields={formFields} errors={errors} touched={touched} disabled={isSubmitting} />
          </Section>
          {children}
          <Section pt={3} title="Configuration" description="Settings and configuration for your charge point.">
            <FormEditable fields={configurationFields} errors={errors} touched={touched} disabled={isSubmitting} />
          </Section>
          <Box pt={2} display="flex">
            <Button color="inherit" onClick={prevStep} variant="contained">
              Back
            </Button>
            <Box mr="auto" />
            <Button color="primary" disabled={isSubmitting} type="submit" variant="contained">
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Next'}
            </Button>
          </Box>
        </FormikForm>
      )}
    </Formik>
  );
}
